import React, { useEffect, useRef } from 'react';

import type { NumberColumnDefinition } from '@gitbook/doc-core';
import { Icon } from '@gitbook/spine-icons';
import { HeadlessTextInput, Tooltip } from '@gitbook/spine-react';

import type { RecordValueRenderProps, TableDefinitionRenderSpec } from './types';

// Character-related constants
const NAVIGATION_KEYS = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'] as const;
const SINGLE_DIGIT_REGEX = /^\d$/;
const MINUS_SIGNS = ['-', '−', '–', '—'] as const;
const DECIMAL_POINTS = ['.', ','] as const;
const VALID_NUMBER_CHARS_REGEX = /^[\d\s\u002D\u2212\u2013\u2014.,]$/;

export const numberDefinitionSpec: TableDefinitionRenderSpec<number, NumberColumnDefinition> = {
    title: 'Number',
    description: '',
    icon: Icon.Numbers,
    ValueComponent: RecordNumberValue,
};

function formatValue(value: number | null): string {
    return value === null || Number.isNaN(value) ? '' : value.toLocaleString();
}

function parseValue(value: string): number | null {
    const parsed = Number.parseFloat(value.replace(/\s/, ''));
    return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Cell content for a Number column.
 */
function RecordNumberValue(
    props: RecordValueRenderProps<number | null, NumberColumnDefinition>
): React.ReactElement {
    const { readOnly, value, onValueChange, selected, view, onSelectionChange, onKeyDown } = props;
    const [localValue, setLocalValue] = React.useState(formatValue(value));
    const [focused, setFocused] = React.useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [attemptedNaN, setAttemptedNaN] = React.useState(false);

    useEffect(() => {
        if (!focused) {
            setLocalValue(formatValue(value));
        }
    }, [value, focused]);

    React.useEffect(() => {
        // If the current cell is `selected`, focus the input
        if (selected) {
            // Delay focus slightly to win the race condition with Slate, otherwise the parent table will steal focus
            const timeoutId = setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
            return () => clearTimeout(timeoutId);
        }
    }, [selected]);

    // If navigating the input or entering numbers, let onChange handle event instead of table's keyboard plugins.
    const interceptKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const key = event.key;
        const input = event.target as HTMLInputElement;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;
        const valueLength = input.value.length;

        // Allow if any modifier key is pressed (for copy/paste etc)
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
            onKeyDown?.(event);
            return;
        }

        // Handle arrow key navigation at input boundaries
        if (key === 'ArrowLeft' && selectionStart === 0 && selectionEnd === 0) {
            event.preventDefault();
            onKeyDown?.(event);
            return;
        }
        if (
            key === 'ArrowRight' &&
            selectionStart === valueLength &&
            selectionEnd === valueLength
        ) {
            event.preventDefault();
            onKeyDown?.(event);
            return;
        }

        const keyIsNumber = key.match(SINGLE_DIGIT_REGEX);
        const keyIsNegativeSign = MINUS_SIGNS.includes(key as (typeof MINUS_SIGNS)[number]);
        const keyIsDecimalPoint = DECIMAL_POINTS.includes(key as (typeof DECIMAL_POINTS)[number]);

        if (
            NAVIGATION_KEYS.includes(key as (typeof NAVIGATION_KEYS)[number]) ||
            keyIsNumber ||
            keyIsNegativeSign ||
            keyIsDecimalPoint
        ) {
            return;
        }

        // Check if the key is a single, non-numeric character.
        const isNonNumericKey = key.length === 1 && !key.match(VALID_NUMBER_CHARS_REGEX);
        if (isNonNumericKey) {
            setAttemptedNaN(true);
        } else {
            setAttemptedNaN(false);
        }

        // Let the table plugins handle any other key events
        event.preventDefault();
        return onKeyDown?.(event);
    };

    const placeholder = view?.type === 'cards' ? 'Enter a number...' : '';

    return readOnly ? (
        <HeadlessTextInput placeholder={placeholder} value={localValue} readOnly />
    ) : (
        <Tooltip
            label="This field uses numeric values"
            isOpen={attemptedNaN}
            position="bottom left"
        >
            <HeadlessTextInput
                ref={inputRef}
                placeholder={placeholder}
                value={localValue}
                onChange={(event) => {
                    setLocalValue(event.target.value);
                    onValueChange(parseValue(event.target.value));
                }}
                onKeyDown={interceptKeydown}
                onFocus={() => {
                    onSelectionChange(true);
                    setFocused(true);
                }}
                onBlur={() => {
                    setFocused(false);
                    setAttemptedNaN(false);
                }}
            />
        </Tooltip>
    );
}
